import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';

const IncidentsTab: React.FC<{ projectId?: number }> = ({ projectId }) => {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<number | null>(null);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['obs-incidents', status, projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.listIncidents({
      ...(status ? { status } : {}),
      ...(projectId ? { project_id: projectId } : {}),
    })).data.data,
    refetchInterval: 60000,
  });

  const detail = useQuery({
    enabled: selected != null,
    queryKey: ['obs-incident', selected],
    queryFn: async () => (await observabilityApi.getIncident(selected!)).data.data,
  });

  const update = async (id: number, payload: any) => {
    await observabilityApi.updateIncident(id, payload);
    qc.invalidateQueries({ queryKey: ['obs-incident', id] });
    qc.invalidateQueries({ queryKey: ['obs-incidents'] });
  };

  if (selected != null) {
    const inc = detail.data;
    return (
      <div>
        <button onClick={() => setSelected(null)} className="text-sm text-indigo-600 mb-3">← Back to incidents</button>
        {!inc ? (
          <div className="text-sm text-cargo-muted">Loading...</div>
        ) : (
          <div className="bg-white rounded-xl border border-cargo-border p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-cargo-muted">Incident #{inc.id}</div>
                <h2 className="text-xl font-semibold mt-1">{inc.title}</h2>
                <div className="text-xs text-cargo-muted mt-1">
                  Opened {inc.opened_at} • Table {inc.table_name}
                </div>
              </div>
              <div className="flex gap-2">
                <SevBadge sev={inc.severity} />
                <StatusBadge status={inc.status} />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => update(inc.id, { status: 'triaged' })}
                className="text-xs px-3 py-1.5 rounded-lg border border-cargo-border hover:bg-gray-50">Mark Triaged</button>
              <button onClick={() => update(inc.id, { status: 'resolved' })}
                className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white">Resolve</button>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">Anomalies in this incident</h3>
              <ul className="space-y-1.5">
                {(inc.anomalies || []).map((a: any) => (
                  <li key={a.id} className="text-sm flex items-start gap-2 p-2 bg-gray-50 rounded">
                    <SevBadge sev={a.severity} />
                    <span>{a.message}</span>
                    <span className="ml-auto text-xs text-cargo-muted">{a.detected_at}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(inc.blast_radius || []).length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Blast Radius</h3>
                <ul className="list-disc pl-5 text-sm">
                  {inc.blast_radius.map((b: string, i: number) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Pill label="All" active={!status} onClick={() => setStatus(undefined)} />
        <Pill label="Open" active={status === 'open'} onClick={() => setStatus('open')} />
        <Pill label="Triaged" active={status === 'triaged'} onClick={() => setStatus('triaged')} />
        <Pill label="Resolved" active={status === 'resolved'} onClick={() => setStatus('resolved')} />
      </div>

      <div className="bg-white rounded-xl border border-cargo-border overflow-hidden">
        {q.isLoading ? (
          <div className="p-8 text-center text-sm text-cargo-muted">Loading...</div>
        ) : (q.data || []).length === 0 ? (
          <div className="p-10 text-center text-cargo-muted text-sm">No incidents.</div>
        ) : (
          <ul>
            {(q.data || []).map((i: any) => (
              <li key={i.id} onClick={() => setSelected(i.id)}
                className="flex items-center gap-3 p-4 border-b border-cargo-border last:border-b-0 hover:bg-gray-50 cursor-pointer">
                <SevBadge sev={i.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{i.title}</div>
                  <div className="text-xs text-cargo-muted">{i.table_name} • Opened {i.opened_at}</div>
                </div>
                <StatusBadge status={i.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Pill: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-lg border ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-cargo-text border-cargo-border hover:bg-gray-50'}`}>
    {label}
  </button>
);

const SevBadge: React.FC<{ sev: string }> = ({ sev }) => {
  const color = sev === 'high' ? 'bg-red-100 text-red-700' : sev === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${color}`}>{sev}</span>;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    open: 'bg-red-100 text-red-700',
    triaged: 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
  };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
};

export default IncidentsTab;
